import React, { useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Tag,
  Popconfirm,
  message,
  Tooltip,
  Divider,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  LinkOutlined,
  DisconnectOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { API } from '@/services/api.service';
import dayjs from 'dayjs';

const { Option } = Select;

const QUERY_KEY = 'automation-companies';

export default function AutomationCompaniesPanel() {
  const { t } = useTranslation(['tables', 'forms', 'messages']);
  const queryClient = useQueryClient();
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [form] = Form.useForm();

  const pa = (key) => t(`payrollAutomation.${key}`, { ns: 'tables' });

  // Fetch automation companies
  const { data: companiesData, isLoading } = useQuery({
    queryKey: [QUERY_KEY],
    queryFn: () => API.payrollAutomation.getAutomationCompanies({ limit: 100 }),
  });

  const companies = companiesData?.data || [];

  // Fetch HLB companies for linking
  const { data: hlbCompaniesData } = useQuery({
    queryKey: ['hlb-companies-dropdown'],
    queryFn: () => API.companies.retrieveDropdownAllCompanies(),
  });

  const hlbCompanies = hlbCompaniesData?.items || [];

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data) => API.payrollAutomation.createAutomationCompany(data),
    onSuccess: () => {
      message.success(t('createSuccess', { ns: 'messages' }));
      queryClient.invalidateQueries([QUERY_KEY]);
      handleCloseModal();
    },
    onError: (error) => {
      message.error(error.response?.data?.message || t('createError', { ns: 'messages' }));
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => API.payrollAutomation.updateAutomationCompany(id, data),
    onSuccess: () => {
      message.success(t('updateSuccess', { ns: 'messages' }));
      queryClient.invalidateQueries([QUERY_KEY]);
      handleCloseModal();
    },
    onError: (error) => {
      message.error(error.response?.data?.message || t('updateError', { ns: 'messages' }));
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => API.payrollAutomation.deleteAutomationCompany(id),
    onSuccess: () => {
      message.success(t('deleteSuccess', { ns: 'messages' }));
      queryClient.invalidateQueries([QUERY_KEY]);
    },
    onError: (error) => {
      message.error(error.response?.data?.message || t('deleteError', { ns: 'messages' }));
    },
  });

  const handleOpenModal = (company = null) => {
    setEditingCompany(company);
    if (company) {
      form.setFieldsValue({
        name: company.name,
        abSoftId: company.abSoftId,
        abSoftCompanyCode: company.abSoftCompanyCode,
        hlbCompanies: (company.hlbCompanies || []).map((c) => c._id || c),
        status: company.status,
        notes: company.notes,
        // Payroll defaults
        salaryType: company.settings?.salaryType || 'net',
        defaultMealAllowance: company.settings?.defaultMealAllowance || 0,
        defaultRegres: company.settings?.defaultRegres || 0,
        payrollCurrency: company.settings?.payrollCurrency || 'EUR',
      });
    } else {
      form.resetFields();
    }
    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setEditingCompany(null);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    // Structure the data with settings nested properly
    const { salaryType, defaultMealAllowance, defaultRegres, payrollCurrency, ...rest } = values;
    const data = {
      ...rest,
      settings: {
        salaryType,
        defaultMealAllowance,
        defaultRegres,
        payrollCurrency,
      },
    };

    if (editingCompany) {
      updateMutation.mutate({ id: editingCompany._id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDelete = (companyId) => {
    deleteMutation.mutate(companyId);
  };

  const columns = [
    {
      title: t('name', { ns: 'tables' }),
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: pa('abSoftId'),
      dataIndex: 'abSoftId',
      key: 'abSoftId',
      render: (text) => text || '-',
    },
    {
      title: pa('hlbLink'),
      dataIndex: 'hlbCompanies',
      key: 'hlbCompanies',
      render: (hlbCompanies) => {
        if (hlbCompanies?.length > 0) {
          return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {hlbCompanies.map((c) => (
                <Tag color="green" icon={<LinkOutlined />} key={c._id || c}>
                  {c.name || pa('linked')}
                </Tag>
              ))}
            </div>
          );
        }
        return (
          <Tag color="default" icon={<DisconnectOutlined />}>
            {pa('notLinked')}
          </Tag>
        );
      },
    },
    {
      title: t('status', { ns: 'tables' }),
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status === 'active' ? pa('active') : pa('inactive')}
        </Tag>
      ),
    },
    {
      title: pa('created'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => dayjs(date).format('DD.MM.YYYY'),
    },
    {
      title: t('actions', { ns: 'tables' }),
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title={pa('edit')}>
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleOpenModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title={pa('deactivateCompany')}
            description={pa('deactivateCompanyDesc')}
            onConfirm={() => handleDelete(record._id)}
            okText={pa('yes')}
            cancelText={pa('no')}
          >
            <Tooltip title={pa('deactivate')}>
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h3 style={{ margin: 0 }}>{pa('automationCompanies')}</h3>
          <p style={{ margin: 0, color: '#666', fontSize: 13 }}>
            {pa('automationCompaniesDesc')}
          </p>
        </div>
        <button
          className="btn-primary"
          onClick={() => handleOpenModal()}
        >
          <PlusOutlined /> {pa('addCompany')}
        </button>
      </div>

      <Table
        columns={columns}
        dataSource={companies}
        rowKey="_id"
        loading={isLoading}
        pagination={{ pageSize: 10 }}
        size="middle"
      />

      <Modal
        title={editingCompany ? pa('editCompany') : pa('addCompany')}
        open={modalVisible}
        onCancel={handleCloseModal}
        footer={null}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ status: 'active' }}
        >
          <Form.Item
            name="name"
            label={pa('companyName')}
            rules={[{ required: true, message: t('required', { ns: 'forms' }) }]}
          >
            <Input placeholder="e.g., Dream Point Distribution doo" />
          </Form.Item>

          <Form.Item
            name="abSoftId"
            label={pa('abSoftId')}
            tooltip={pa('abSoftIdTooltip')}
          >
            <Input placeholder="e.g., DPD001" />
          </Form.Item>

          <Form.Item
            name="abSoftCompanyCode"
            label={pa('abSoftCompanyCode')}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="hlbCompanies"
            label={pa('linkToHlb')}
          >
            <Select
              mode="multiple"
              placeholder={pa('selectHlbCompanies')}
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {hlbCompanies.map((company) => (
                <Option key={company._id} value={company._id}>
                  {company.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="projectCode"
            label={pa('projectCode')}
            tooltip={pa('projectCodeTooltip')}
          >
            <Input placeholder="METRO_L1P1D" style={{ textTransform: 'uppercase' }} />
          </Form.Item>

          {editingCompany && (
            <Form.Item
              name="status"
              label={t('status', { ns: 'tables' })}
            >
              <Select>
                <Option value="active">{pa('active')}</Option>
                <Option value="inactive">{pa('inactive')}</Option>
              </Select>
            </Form.Item>
          )}

          <Divider orientation="left" style={{ fontSize: 13 }}>{pa('payrollDefaults')}</Divider>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Form.Item
              name="salaryType"
              label={pa('salaryType')}
              initialValue="net"
            >
              <Select>
                <Option value="net">{pa('net')}</Option>
                <Option value="gross">{pa('gross')}</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="payrollCurrency"
              label={pa('currency')}
              initialValue="EUR"
            >
              <Select>
                <Option value="EUR">EUR</Option>
                <Option value="RSD">RSD</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="defaultMealAllowance"
              label={pa('mealAllowance')}
              initialValue={0}
            >
              <InputNumber style={{ width: '100%' }} min={0} />
            </Form.Item>

            <Form.Item
              name="defaultRegres"
              label={pa('regres')}
              initialValue={0}
            >
              <InputNumber style={{ width: '100%' }} min={0} />
            </Form.Item>
          </div>

          <Form.Item
            name="notes"
            label={pa('notes')}
          >
            <Input.TextArea rows={3} />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <button className="btn-secondary" type="button" onClick={handleCloseModal}>{pa('cancel')}</button>
              <Button
                type="primary"
                htmlType="submit"
                className="btn-primary"
                loading={createMutation.isPending || updateMutation.isPending}
              >
                {editingCompany ? pa('update') : pa('create')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
